// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DataTable from "examples/Tables/DataTable";

// Data
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import { Button, Grid, IconButton, Tooltip } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDLink from "components/MDLink";
import MDButton from "components/MDButton";
import DefaultCell from "examples/Tables/components/DefaultCell";
import { LoginFormContext } from "context/loginFormCtx";
import { Delete, Edit } from "@mui/icons-material";
import moment from "moment";
import { TABLE_FILTERS } from "utils/constants";
import {
  getFiltersFromCache,
  saveFiltersToCache,
  getSortByFromCache,
  saveSortByToCache,
} from "utils/cacheManager";
import { getTenantAdmin, deleteTenantAdmin } from "lib/Api/tenant-admin";
import TenantAdminDeleteDialog from "components/TenantAdminDeleteDialog";

// Function to format plain digits to formatted phone number
export const formatToPhoneNumber = (number) => {
  // First, ensure we only have digits and exactly 10 of them
  const cleaned = number.replace(/\D/g, "").slice(0, 10);

  let maskedValue = "";

  if (cleaned.length <= 3) {
    maskedValue = cleaned;
  } else if (cleaned.length <= 6) {
    maskedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    maskedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  // Format the number
  return maskedValue;
};

// Function to convert formatted phone number to plain digits
export const formatPhoneNumber = (phoneNumber) =>
  // Remove all non-digit characters using regex
  phoneNumber.replace(/\D/g, "");

export const handleMasking = (event, id, setValue) => {
  const input = event.target;
  const originalValue = input.value;
  const startPos = input.selectionStart; // Capture cursor position
  const value = originalValue.replace(/\D/g, "");
  let maskedValue = "";

  if (value.length <= 3) {
    maskedValue = value;
  } else if (value.length <= 6) {
    maskedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
  } else {
    maskedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
  }

  // @ts-ignore
  setValue(id, maskedValue, {
    shouldValidate: true,
  });

  // Update the input value to the masked one
  input.value = maskedValue;

  // Adjust cursor position based on deletion
  let newCursorPos = startPos;
  if (originalValue.length > maskedValue.length) {
    // Character deleted
    newCursorPos = startPos - (originalValue.length - maskedValue.length);
  } else {
    // Character added
    newCursorPos = startPos + (maskedValue.length - originalValue.length);
  }

  // Restore the cursor position
  input.setSelectionRange(newCursorPos, newCursorPos);
};

function TenantAdmins({ tenantDetails }) {
  const { setNotification, showMessageNotification } = useContext(LoginFormContext);
  const navigate = useNavigate();
  const { id: tenantId } = useParams();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  const handleAdminDataAssignment = (adminData) => {
    setAdminToDelete(adminData);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async (adminData) => {
    showMessageNotification({
      title: "Are you sure?",
      text: "You want to delete this tenant admin?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--cc-confirm-button-color)",
      cancelButtonColor: "var(--cc-cancel-button-color)",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteTenantAdmin(adminData.id, {
          tenant_id: tenantId,
          db_schema: tenantDetails?.tenantData?.db_schema,
        });

        if (response?.status === 200) {
          getAdminData();
          getAllAdmins();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
        } else {
          if (response?.status === 409) {
            handleAdminDataAssignment(adminData);
          } else {
            setNotification({
              color: "error",
              icon: "cancel",
              title: "Error",
              content: response.data.message,
              open: true,
            });
          }
        }
      }
    });
  };

  const handleEntityAssignedTo = async (adminId, entityAssignedTo) => {
    if (!entityAssignedTo?.id) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Please select a tenant admin to reassign entities to",
        open: true,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await deleteTenantAdmin(adminId, {
        tenant_id: tenantId,
        db_schema: tenantDetails?.tenantData?.db_schema,
        assignedTo: entityAssignedTo.id,
      });

      if (response?.status === 200) {
        getAdminData();
        getAllAdmins();
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: response.data.message,
          open: true,
        });
        handleCloseDeleteDialog();
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data.message,
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "An error occurred while deleting the tenant admin",
        open: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setAdminToDelete(null);
    setIsDeleting(false);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(getFiltersFromCache(TABLE_FILTERS.TENANT_ADMINS) || []);
  const [sortBy, setSortBy] = useState(
    getSortByFromCache(TABLE_FILTERS.TENANT_ADMINS) || [{ id: "first_name", desc: false }]
  );
  const [allAdmins, setAllAdmins] = useState([]);

  const dataTableColumns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "first_name",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Last Name",
        accessor: "last_name",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Email",
        accessor: "email",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value || "-"} />,
      },
      {
        Header: "Phone",
        accessor: "phone",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value ? formatToPhoneNumber(value) : "-"} />,
      },
      // {
      //   Header: "Created By",
      //   accessor: "created_by",
      //   filterInputType: "text",
      //   sort: true,
      //   Cell: ({ value }) => <DefaultCell value={value} />,
      // },
      // {
      //   Header: "Created At",
      //   accessor: "created_at",
      //   filterInputType: "date",
      //   disableFuture: true,
      //   sort: true,
      //   Cell: ({ value }) => <DefaultCell value={moment(value).format("MM/DD/YYYY")} />,
      // },
      {
        Header: "Action",
        accessor: "actions",
        width: "3%",
        sort: false,
        Cell: ({ value, row }) => (
          <MDBox>
            <Tooltip title="Edit Tenant Admin" placement="top">
              <IconButton
                onClick={() => {
                  navigate(`/tenants/${tenantId}/admins/${row.original.id}/edit`, {
                    state: {
                      db_schema: tenantDetails?.tenantData?.db_schema,
                      client_id: tenantDetails?.userData?.client_id,
                    },
                  });
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
            {rowCount > 1 && (
              <Tooltip title="Delete Tenant Admin" placement="top">
                <IconButton
                  onClick={() => {
                    handleDelete(row.original);
                    // handleDeleteAdmin(row.original);
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </MDBox>
        ),
      },
    ],
    [rowCount]
  );
  const [tableData, setTableData] = useState({
    columns: dataTableColumns,
    rows: [],
  });

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
    setPage(0);
  };

  const handleFiltersChange = (filterObject) => {
    setFilters(filterObject);
    saveFiltersToCache(TABLE_FILTERS.TENANT_ADMINS, filterObject);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    saveFiltersToCache(TABLE_FILTERS.TENANT_ADMINS, []);
    setPage(0);
  };

  const handleSortByChange = (sortObject) => {
    setSortBy(sortObject);
    saveSortByToCache(TABLE_FILTERS.TENANT_ADMINS, sortObject);
  };

  const getAdminData = async () => {
    try {
      setIsLoading(true);
      const sortId = sortBy[0]?.id;
      const sortVal = sortBy[0]?.desc ? "desc" : "asc";
      const updatedFilters = filters.map((filter) => {
        return filter;
      });
      const filterBody = {
        page: page + 1,
        pageSize,
        filters: updatedFilters,
        sortBy: sortId ? [{ id: sortId, value: sortVal }] : [],
        tenant_id: tenantId,
        db_schema: tenantDetails?.tenantData?.db_schema,
      };
      const response = await getTenantAdmin(filterBody);
      if (response && response.status === 200) {
        setTableData({
          ...tableData,
          rows: response.data.data.rows,
        });
        setRowCount(response?.data?.data?.count || 0);
        setTotalPages(Math.ceil(response.data.data.count / pageSize));
      }
    } catch (error) {
      console.log("error (getAdminData): ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllAdmins = async () => {
    try {
      const filterBody = {
        filters: [],
        sortBy: [{ id: "first_name", value: "asc" }],
        tenant_id: tenantId,
        db_schema: tenantDetails?.tenantData?.db_schema,
      };
      const response = await getTenantAdmin(filterBody);
      if (response && response.status === 200) {
        const admins = response.data.data.rows?.map((admin) => {
          return {
            id: admin.id,
            label: `${admin?.first_name || ""} ${admin?.last_name || ""}`,
          };
        });
        setAllAdmins(admins);
      }
    } catch (error) {}
  };

  useEffect(() => {
    setTableData({
      ...tableData,
      columns: dataTableColumns,
    });
    // eslint-disable-next-line
  }, [dataTableColumns]);

  useEffect(() => {
    getAdminData();
    // eslint-disable-next-line
  }, [page, pageSize, filters, sortBy]);

  useEffect(() => {
    getAllAdmins();
    // eslint-disable-next-line
  }, []);

  return (
    <MDBox pt={3} pb={3}>
      <Card>
        <MDBox p={3} lineHeight={1}>
          <Grid container justifyContent="space-between" spacing={3}>
            <Grid item>
              <MDTypography variant="h5" fontWeight="medium">
                Tenant Admins
              </MDTypography>
              <MDTypography variant="button" color="text">
                Here you can manage all the tenant admins.
              </MDTypography>
            </Grid>
            <Grid item>
              <MDLink
                to={`/tenants/${tenantId}/admins/add`}
                state={{
                  db_schema: tenantDetails?.tenantData?.db_schema,
                  client_id: tenantDetails?.userData?.client_id,
                }}
              >
                <Button variant="outlined" startIcon={<AddIcon />}>
                  Add New Tenant Admin
                </Button>
              </MDLink>
            </Grid>
          </Grid>
        </MDBox>
        <DataTable
          isLoading={isLoading}
          table={tableData}
          pageSize={pageSize}
          currentPage={page}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          filters={filters}
          handleFiltersChange={handleFiltersChange}
          sorting={sortBy}
          handleSortByChange={handleSortByChange}
          getData={getAdminData}
          handleClearAllFilters={handleClearAllFilters}
          loading={isLoading}
          rowCount={rowCount}
        />
      </Card>

      <TenantAdminDeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        adminToDelete={adminToDelete}
        allAdmins={allAdmins}
        onDelete={handleEntityAssignedTo}
        isDeleting={isDeleting}
      />
    </MDBox>
  );
}

export default TenantAdmins;
